'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Zap, Shield, TrendingUp, Star } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import DemoCard from './DemoCard';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';

type LoginForm = { email: string; password: string; remember: boolean };
type SignupForm = { name: string; email: string; password: string; confirm: string; terms: boolean };

const DEMO_CREDENTIALS = [
  { role: 'Admin', email: 'admin@footballdash.app', password: 'Admin#2026' },
];

export default function AuthScreen() {
  const router = useRouter();
  const { language } = useLanguage();
  const t = getTranslations(language);
  const supabase = createClient();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupForm>({ defaultValues: { name: '', email: '', password: '', confirm: '', terms: false } });

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const autofill = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    toast.success('Credentials filled in!');
  };

  const onLogin = loginForm.handleSubmit(async (data) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });
      if (error) {
        loginForm.setError('email', { message: error.message });
        setLoading(false);
        return;
      }
      toast.success('Welcome back!');
      setTimeout(() => router.push('/player-dashboard'), 500);
    } catch {
      loginForm.setError('email', { message: 'Login failed. Please try again.' });
      setLoading(false);
    }
  });

  const onSignup = signupForm.handleSubmit(async (data) => {
    if (data.password !== data.confirm) {
      signupForm.setError('confirm', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    try {
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: { full_name: data.name, role: 'user' },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) {
        signupForm.setError('email', { message: error.message });
        setLoading(false);
        return;
      }

      // Send welcome email via edge function
      try {
        await supabase.functions.invoke('send-welcome-email', {
          body: {
            email: data.email,
            name: data.name,
          },
        });
      } catch {
        // Welcome email failure is non-blocking
      }

      toast.success('Account created! Set up your player profile.');
      setTimeout(() => router.push('/player-profile-setup'), 500);
    } catch {
      signupForm.setError('email', { message: 'Signup failed. Please try again.' });
      setLoading(false);
    }
  });

  return (
    <div className="min-h-screen flex bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />

      {/* LEFT PANEL — Brand Hero */}
      <div className="hidden lg:flex lg:w-[55%] xl:w-[58%] flex-col relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #050810 0%, #0d1520 40%, #0a1830 100%)' }}>

        {/* Background grid */}
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(200,168,75,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(200,168,75,0.3) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />

        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #c8a84b, transparent)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #1a3a5c, transparent)', filter: 'blur(40px)' }} />

        {/* Logo */}
        <div className="relative z-10 p-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center ovr-badge">
              <span className="text-lg font-black text-primary-foreground">FD</span>
            </div>
            <span className="text-xl font-bold text-foreground tracking-tight">FootballDash</span>
          </div>
          <LanguageSwitcher />
        </div>

        {/* Card hero */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-12 pb-12">
          <div className="animate-float">
            <DemoCard />
          </div>

          <div className="mt-10 text-center space-y-3">
            <h1 className="text-4xl xl:text-5xl font-black tracking-tight shimmer-text leading-tight">
              {t.yourPlayerCard}<br />{t.yourLegacy}
            </h1>
            <p className="text-muted-foreground text-lg max-w-sm mx-auto leading-relaxed">
              {t.heroDesc}
            </p>
          </div>

          {/* Feature pills */}
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {[
              { icon: <Zap size={14} />, text: 'OVR Calculator' },
              { icon: <Star size={14} />, text: 'Ultimate Card' },
              { icon: <TrendingUp size={14} />, text: 'AI Coach' },
              { icon: <Shield size={14} />, text: 'Progress Tracking' },
            ].map((pill) => (
              <div key={`pill-${pill.text}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium position-badge">
                {pill.icon}
                {pill.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT PANEL — Auth Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 relative">
        <div className="w-full max-w-md">

          {/* Mobile logo + language */}
          <div className="lg:hidden flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center ovr-badge">
                <span className="text-sm font-black text-primary-foreground">FD</span>
              </div>
              <span className="text-lg font-bold text-foreground">FootballDash</span>
            </div>
            <LanguageSwitcher />
          </div>

          {/* Mode toggle */}
          <div className="flex rounded-xl p-1 mb-8 bg-muted">
            {(['login', 'signup'] as const).map((m) => (
              <button key={`mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  mode === m
                    ? 'bg-primary text-primary-foreground shadow-gold'
                    : 'text-muted-foreground hover:text-foreground'
                }`}>
                {m === 'login' ? t.signIn : t.createAccount}
              </button>
            ))}
          </div>

          {/* Heading */}
          <div className="mb-7">
            <h2 className="text-2xl font-bold text-foreground">
              {mode === 'login' ? t.welcomeBack : t.createYourProfile}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              {mode === 'login' ? t.signInDesc : t.signUpDesc}
            </p>
          </div>

          {/* LOGIN FORM */}
          {mode === 'login' && (
            <form onSubmit={onLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t.emailAddress}</label>
                <input
                  type="email"
                  className="input-field"
                  placeholder="you@footballdash.app"
                  {...loginForm.register('email', { required: 'Email is required' })}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-red-400 text-xs mt-1.5">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t.password}</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    className="input-field pr-10"
                    placeholder="••••••••"
                    {...loginForm.register('password', { required: 'Password is required' })}
                  />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-red-400 text-xs mt-1.5">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded accent-primary"
                    {...loginForm.register('remember')} />
                  <span className="text-sm text-muted-foreground">{t.rememberMe}</span>
                </label>
                <button type="button" className="text-sm text-primary hover:text-accent transition-colors">
                  {t.forgotPassword}
                </button>
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    {t.signingIn}
                  </>
                ) : t.signIn}
              </button>
            </form>
          )}

          {/* SIGNUP FORM */}
          {mode === 'signup' && (
            <form onSubmit={onSignup} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t.fullName}</label>
                <input type="text" className="input-field" placeholder="Your Name"
                  {...signupForm.register('name', { required: 'Name is required' })} />
                {signupForm.formState.errors.name && (
                  <p className="text-red-400 text-xs mt-1.5">{signupForm.formState.errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t.emailAddress}</label>
                <input type="email" className="input-field" placeholder="you@footballdash.app"
                  {...signupForm.register('email', { required: 'Email is required' })} />
                {signupForm.formState.errors.email && (
                  <p className="text-red-400 text-xs mt-1.5">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t.password}</label>
                <div className="relative">
                  <input type={showPass ? 'text' : 'password'} className="input-field pr-10" placeholder="••••••••"
                    {...signupForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })} />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-red-400 text-xs mt-1.5">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t.confirmPassword}</label>
                <div className="relative">
                  <input type={showConfirm ? 'text' : 'password'} className="input-field pr-10" placeholder="••••••••"
                    {...signupForm.register('confirm', { required: 'Please confirm password' })} />
                  <button type="button" onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirm && (
                  <p className="text-red-400 text-xs mt-1.5">{signupForm.formState.errors.confirm.message}</p>
                )}
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 mt-0.5 rounded accent-primary"
                  {...signupForm.register('terms', { required: 'You must agree to terms' })} />
                <span className="text-xs text-muted-foreground">{t.agreeTerms}</span>
              </label>
              {signupForm.formState.errors.terms && (
                <p className="text-red-400 text-xs">{signupForm.formState.errors.terms.message}</p>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    {t.creatingAccount}
                  </>
                ) : t.createAccount}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}